import Filters from './Filters/Filters';
import { useCallback, useEffect, useMemo, useState } from 'react';
import expandMore from '../../../../../../assets/expand-more.svg';
import styles from './Report.module.scss';
import clsx from 'clsx';
import Graph from '../../../../components/Graph/Graph';
import TypeSelector from '../../../../components/TypeSelector/TypeSelector';
import { DASHBOARD_DATA_TYPES, dataTypeSelectorOptions } from '../../../..';
import CreditSummaryTable from '../CreditSummaryTable/CreditSummaryTable';
import CreditSummaryBar from './CreditSummaryBar/CreditSummaryBar';
import AicaOverview from '../../../../../../common/AicaOverview/AicaOverview';
import { BackButtonLeftFill, infoToolTip } from '../../../../../../assets/hostedassets';
import BureauEnquiryDrawer from './BureauEnquiryDrawer/BureauEnquiryDrawer';
import useReport from './useReport';
import { ENQUIRY_BANNER_LINK_LABEL, enquiryBannerText, NEW_TO_CREDIT_THRESHOLD } from './CreditBureauConstants';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_CIBIL_VIEW_SWITCHED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import FilterTriggerButton from '../../../../../../common/FilterTriggerButton/FilterTriggerButton';
import DataTableFilterDrawer from '../../../../../../common/_custom/DataTable/components/DataTableFilterDrawer/DataTableFilterDrawer';
import { DataTableColumn } from '../../../../../../common/_custom/DataTable/DataTable.types';
import { hasFilterableColumns } from '../../../../../../common/_custom/DataTable/utils';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { setCustomFilterDrawer } from '../../../../../../store/investor/action';
import { CREDIT_SUMMARY_FILTER_SOURCE_KEY } from '../CreditSummaryTable/CreditSummaryTable.constants';
import { hasActiveCreditSummaryFilters } from '../CreditSummaryTable/useCreditSummaryRows';

import dayjs from 'dayjs';

const BREADCRUMB_ROOT_LABEL = 'Director Bureau';

const FILTER_IDLE_LABEL = 'Add Filters';
const FILTER_ACTIVE_LABEL = 'Filters Applied';

function Report(props) {
    const {
        //State
        dataType,
        isBureauEnquiryDrawerOpen,
        searchParams,
        setSelectedReport,
        resetQueryParams,
        selectedReportConfig,
        bureauType,
        directorId,
        selectedDirector,
        selectedReport,
        cibilOverviewData,
        chartsData,
        user,
        isCombinedReport,
        lastConsent,

        //Methods
        setDataType,
        openBureauEnquiryDrawer,
        closeBureauEnquiryDrawer,
        getReportTitle,
        getImageSource,
        fetchGraph,
        enquiryCount,
        totalEnquiryCount,
    } = useReport(props);

    const dispatch = useAppDispatch();

    /* Filter columns are owned by the table (they are derived from the response it fetches) but the
       trigger and drawer live up here, so they can sit in the header row beside the Chart/Table
       toggle. Only the column DEFINITIONS travel upward — the applied values live in redux, so
       there is no filter state duplicated across the two components. */
    const [filterColumns, setFilterColumns] = useState<DataTableColumn[]>([]);
    const [showFilters, setShowFilters] = useState(false);

    const appliedFilters = useAppSelector((state) => state.investor.customFilterDrawer?.appliedFilters?.[CREDIT_SUMMARY_FILTER_SOURCE_KEY]);
    const hasActiveFilters = useMemo(() => hasActiveCreditSummaryFilters(appliedFilters), [appliedFilters]);

    /* Stable identity so the table's publish effect doesn't re-fire on every Report render. */
    const onColumnsResolved = useCallback((columns: DataTableColumn[]) => setFilterColumns(columns), []);

    const applyFilters = useCallback(
        (filters: Record<string, any>) => {
            /* `open: false` mirrors what Leads dispatches on apply. This drawer's visibility is local
               state, so the redux flag is only kept closed — never toggled from here, which would
               reach any other mounted DataTable's own drawer. The reducer merges appliedFilters by
               source key, so this cannot disturb LEADS_PAGE or the Deals views. */
            dispatch(setCustomFilterDrawer({ open: false, appliedFilters: { [CREDIT_SUMMARY_FILTER_SOURCE_KEY]: filters } }));
        },
        [dispatch],
    );

    const clearFilters = useCallback(() => applyFilters({}), [applyFilters]);

    /* Applied filters live in redux, which outlives this report. Without this reset, filtering one
       director's accounts and then opening another director (or switching bureau) would carry the
       old selection over — and since a lender on one file need not appear on another, the second
       table could come up empty for no visible reason. Keyed on the report's identity, so toggling
       Chart/Table keeps the user's filters (this component stays mounted across that switch). */
    const reportIdentity = `${props.selectedCompany?.assesseeOrgId}|${directorId}|${bureauType}|${selectedReport}`;
    useEffect(() => {
        clearFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportIdentity]);

    /* The control is meaningless over the Chart view, and pointless before the response has told us
       which columns exist. */
    const showFilterButton = dataType === DASHBOARD_DATA_TYPES.TABLE && hasFilterableColumns(filterColumns);

    const renderTableSection = () => {
        return (
            <div className={styles.TableDataTypeSection}>
                <CreditSummaryTable assesseeOrgId={props.selectedCompany?.assesseeOrgId} onColumnsResolved={onColumnsResolved} />
            </div>
        );
    };

    const isNewToCredit = () => {
        const minScore = cibilOverviewData?.creditScoreMin;
        const maxScore = cibilOverviewData?.creditScoreMax;
        const primaryScore = maxScore || minScore;
        return typeof primaryScore == 'number' && primaryScore < NEW_TO_CREDIT_THRESHOLD;
    };

    const renderChartsSection = () => {
        return (
            <div className={styles.ChartDataTypeSection}>
                <div className={styles.Section}>
                    <div className={styles.TopCard}>
                        <div className={styles.ChartsPanel}>
                            <div className={styles.Card}>
                                <div className={styles.Metrics}>
                                    <div>
                                        Total active loan accounts
                                        <div className={clsx(styles.MetricValue, styles.GrayPill)}>
                                            {cibilOverviewData?.totalActiveAccounts}
                                        </div>
                                    </div>
                                    <div>
                                        Total sanction amount (as of today)
                                        <div className={clsx(styles.MetricValue, styles.GrayPill)}>
                                            ₹
                                            {cibilOverviewData?.totalSanctionedAmount?.toLocaleString('en-IN', {
                                                maximumFractionDigits: 2,
                                            })}
                                            L
                                        </div>
                                    </div>
                                    <div>
                                        Credit utilisation (as of today)
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.creditUtilisation || 0) < 30,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.creditUtilisation || 0) >= 30,
                                            })}
                                        >
                                            {Math.round(parseFloat(`${cibilOverviewData?.creditUtilisation}`))}%
                                        </div>
                                    </div>
                                    <div>
                                        Enquiries in past 90 days
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.enquiriesInPast90Days || 0) < 10,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.enquiriesInPast90Days || 0) >= 10,
                                            })}
                                        >
                                            {cibilOverviewData?.enquiriesInPast90Days} enquiries
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.Card}>
                                <div className={styles.Metrics}>
                                    <div>
                                        No. of accounts with DPDs
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.noOfDpdAccounts || 0) <= 0,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.noOfDpdAccounts || 0) > 0,
                                            })}
                                        >
                                            {cibilOverviewData?.noOfDpdAccounts} accounts
                                        </div>
                                    </div>
                                    <div>
                                        Total no. of DPDs recorded
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.totalDpdsRecorded || 0) <= 0,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.totalDpdsRecorded || 0) > 0,
                                            })}
                                        >
                                            {cibilOverviewData?.totalDpdsRecorded} days
                                        </div>
                                    </div>
                                    <div>
                                        Current overdue amount
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.totalOverdueAmount || 0) <= 0,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.totalOverdueAmount || 0) > 0,
                                            })}
                                        >
                                            ₹
                                            {cibilOverviewData?.totalOverdueAmount?.toLocaleString('en-IN', {
                                                maximumFractionDigits: 2,
                                            })}
                                            L
                                        </div>
                                    </div>
                                    <div>
                                        Highest DPD recorded till date (no. of days)
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.highestDpdRecorded || 0) <= 0,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.highestDpdRecorded || 0) > 0,
                                            })}
                                        >
                                            {cibilOverviewData?.highestDpdRecorded} days
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.ChartSections}>
                    {selectedReportConfig?.chartPanel?.sections?.map((section, i) => (
                        <div className={styles.ChartSection} key={`${section.title}-${i}`}>
                            {/* <div className={styles.SectionTitle}>{section.title}</div> */}
                            <div className={styles.GraphsContainer}>
                                {section?.cards?.map((chartName, i) => (
                                    <div
                                        key={`${chartName}-${i}`}
                                        style={{
                                            gridColumn: chartsData?.[chartName]?.metadata?.tileSize === 2 ? '1/3' : '',
                                        }}
                                    >
                                        {!!chartsData?.[chartName] && (
                                            <Graph
                                                key={`${chartName}-${i}`}
                                                graphData={chartsData[chartName]}
                                                graphName={chartName}
                                                fetchGraph={() => fetchGraph(chartName)}
                                                selectedPeriod={''}
                                                user={user}
                                                showAlerts={true}
                                                horizontalChart={chartsData?.[chartName]?.metadata?.tileSize === 2}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const getRenderComponent = () => {
        switch (dataType) {
            case DASHBOARD_DATA_TYPES.CHARTS:
                return <>{renderChartsSection()}</>;
            case DASHBOARD_DATA_TYPES.TABLE:
                return <>{renderTableSection()}</>;
            default:
                return 'no';
        }
    };

    return (
        <div className={styles.Report}>
            <div className={styles.Breadcrumb}>
                <div>
                    <span
                        className={styles.Blue}
                        onClick={() => {
                            setSelectedReport('');
                            resetQueryParams();
                        }}
                    >
                        <img className={styles.BackArrow} src={BackButtonLeftFill} alt="" />
                        {BREADCRUMB_ROOT_LABEL}
                    </span>
                    <img src={expandMore} alt="" height="10px" />
                    <span className={styles.ReportTitle}>{getReportTitle()}</span>
                </div>
                <div>
                    <CreditSummaryBar
                        minScore={cibilOverviewData?.creditScoreMin}
                        maxScore={cibilOverviewData?.creditScoreMax}
                        selectedReport={selectedReport}
                    />
                    <img src={getImageSource(selectedDirector, searchParams.get('directorBureauType'))} alt="directorBureauType" />
                    {!isCombinedReport && (
                        <div className={styles.LastConsent}>
                            {lastConsent
                                ? `Last consent by ${lastConsent.consenterName} on ${dayjs(lastConsent.consentedAt).format('DD MMM YYYY')}`
                                : 'No Consent Given'}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.ReportBody}>
                <div className={styles.TypeSelector}>
                    <TypeSelector
                        options={dataTypeSelectorOptions}
                        selectedOptionId={dataType}
                        changeSelectedOption={(id) => {
                            Mixpanel.track(AICA_CIBIL_VIEW_SWITCHED, { view: id });
                            setDataType(id);
                        }}
                    />
                    <div className={styles.RightControls}>
                        {/* Same trigger as the Leads header — shared component, so the two screens
                            cannot drift. */}
                        {showFilterButton && (
                            <FilterTriggerButton
                                hasActiveFilters={hasActiveFilters}
                                onOpen={() => setShowFilters(true)}
                                onClear={clearFilters}
                                idleLabel={FILTER_IDLE_LABEL}
                                activeLabel={FILTER_ACTIVE_LABEL}
                            />
                        )}
                    </div>
                </div>

                {totalEnquiryCount > 0 && (
                    <div className={styles.EnquiryBanner}>
                        <img src={infoToolTip} alt="" />
                        <span>
                            {enquiryBannerText(enquiryCount, totalEnquiryCount)}{' '}
                            <button type="button" className={styles.EnquiryBannerLink} onClick={openBureauEnquiryDrawer}>
                                {ENQUIRY_BANNER_LINK_LABEL}
                            </button>
                        </span>
                    </div>
                )}

                <AicaOverview
                    module="cibilIndividual"
                    style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}
                    resourceId={searchParams.get('directorId') as string}
                />
                {getRenderComponent()}
            </div>

            {/* No width override — the Leads page's drawer (rendered inside DataTable) passes none,
                so it uses the component's 40% default. Same here keeps the two drawers identical. */}
            <DataTableFilterDrawer
                open={showFilters}
                onClose={() => setShowFilters(false)}
                columns={filterColumns}
                onApply={applyFilters}
                filterSourceKey={CREDIT_SUMMARY_FILTER_SOURCE_KEY}
            />

            {isBureauEnquiryDrawerOpen && <BureauEnquiryDrawer onClose={closeBureauEnquiryDrawer} />}
        </div>
    );
}

export default Report;
